import { Box, Flex, FormControl, FormErrorMessage, Input, Text } from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { PLAN_TEXT } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import { ActionSteponeSchema } from 'validation/schemas/ActionStepSchema';
import {
  addSteponeFormData,
  getActionList,
  getFormValues,
  handleActionStepNext,
} from 'redux/DataImportSlice';

import {
  ACTION_COLOR_OPTIONS,
  getFallbackActionColor,
  getStoredActionColor,
} from '../actionColors';
import colors from '../dataTheme';

import ActionStepFooter from './ActionStepFooter';
import { FieldLabel, StepHeading, stepInputStyle } from './actionStepUi';

interface Properties {
  setHelpModalSeleceted: any;
  setactionCreate?: any;
}

const ActionStepOne = ({ setactionCreate }: Properties) => {
  const dispatch: any = useDispatch();
  const getFormValue = useSelector(getFormValues);
  const actionList: any[] = useSelector(getActionList) ?? [];
  const editState = useSelector((state: any) => state.dataImportSlice.isEdit);
  // A new action defaults to the next palette colour so neighbouring chips differ.
  const initialColor =
    getFormValue.color ??
    (editState?.isEditForm
      ? getStoredActionColor(editState?.actionId) ?? getFallbackActionColor(editState?.actionId)
      : ACTION_COLOR_OPTIONS[actionList.length % ACTION_COLOR_OPTIONS.length]);
  const handleSubmit = (form: any, setSubmitting: (data: boolean) => void) => {
    dispatch(addSteponeFormData(form));
    setSubmitting(false);
    dispatch(handleActionStepNext());
  };

  return (
    <Box w='100%'>
      <StepHeading title='Name your action' subtitle='Give it a name you will recognise later.' />
      <Formik
        initialValues={{
          name: '' || getFormValue.name,
          description: '' || getFormValue.description,
          color: initialColor,
        }}
        validationSchema={ActionSteponeSchema}
        onSubmit={(form, { setSubmitting }) => {
          handleSubmit(form, setSubmitting);
        }}
        enableReinitialize={true}
      >
        {({ errors, touched, isSubmitting, values, setFieldValue }: any) => (
          <Form style={{ width: '100%' }}>
            <FormControl isInvalid={Boolean(errors.name && touched.name)}>
              <FieldLabel>Name</FieldLabel>
              <Field
                as={Input}
                name='name'
                type='text'
                placeholder='Name'
                autoComplete='off'
                disabled={isSubmitting}
                {...stepInputStyle}
              />
              <FormErrorMessage {...PLAN_TEXT.labelSm}>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl mt={4}>
              <FieldLabel>Description</FieldLabel>
              <Field
                as={Input}
                name='description'
                type='text'
                placeholder='Description (optional)'
                autoComplete='off'
                disabled={isSubmitting}
                {...stepInputStyle}
              />
            </FormControl>

            <Box mt={4}>
              <FieldLabel>Colour</FieldLabel>
              <Flex gap={2} alignItems='center'>
                {ACTION_COLOR_OPTIONS.map((option) => {
                  const isSelected = values.color === option;
                  return (
                    <Flex
                      key={option}
                      as='button'
                      type='button'
                      role='radio'
                      aria-checked={isSelected}
                      aria-label={`Colour ${option}`}
                      w='24px'
                      h='24px'
                      alignItems='center'
                      justifyContent='center'
                      borderRadius='full'
                      border='2px solid'
                      borderColor={isSelected ? option : 'transparent'}
                      transition='border-color 0.12s ease'
                      onClick={() => setFieldValue('color', option)}
                    >
                      <Box w='14px' h='14px' borderRadius='full' bg={option} />
                    </Flex>
                  );
                })}
              </Flex>
              <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary} mt='6px'>
                Shown as the dot next to this action.
              </Text>
            </Box>

            <Flex justifyContent='flex-end' pt={6}>
              <ActionStepFooter
                prevButtonName='Cancel'
                nextButtonName='Continue'
                isSubmitting={isSubmitting}
                setactionCreate={setactionCreate}
              />
            </Flex>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default ActionStepOne;
